import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { createStoresWithStorage } from '../stores/childNavStores';
import { StorageInterface } from '../stores/childNavStores';
import { createRespondentSessionStore, createNamespacedStorage } from '../stores/respondentSessionStore';
import { createContentStore } from '../stores/contentStore';
import type { SecureLoginResponseDto } from '../types/api/tokenAssessment';

// Define the store context type
type StoresType = ReturnType<typeof createStoresWithStorage>;

// Cache the React context on globalThis so that Vite HMR module re-evaluations
// reuse the same context object rather than creating a new one. Without this,
// after any hot reload the StoreProvider hands a value to the OLD context while
// child components read from the NEW context → "useStores must be used within a
// StoreProvider" error at runtime.
const _g = globalThis as any;
if (!_g.__childNavStoreContext) {
  _g.__childNavStoreContext = createContext<StoresType | null>(null);
}
const StoreContext: React.Context<StoresType | null> = _g.__childNavStoreContext;

interface StoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  apiService?: any; // ApiService type
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ 
  children, 
  storage, 
  apiService 
}) => {
  // Create stores with the provided storage
  const stores = React.useMemo(() => {
    const storeInstances = createStoresWithStorage(storage);
    
    // Set API service if provided
    if (apiService && storeInstances.setApiService) {
      storeInstances.setApiService(apiService);
    }
    
    return storeInstances;
  }, [storage, apiService]);

  // Initialize app on mount
  React.useEffect(() => {
    console.log('🔧 StoreProvider: Effect triggered, stores available:', !!stores);
    
    if (stores && (stores as any).executeBootSequence) {
      console.log('🔧 StoreProvider: Calling executeBootSequence(startup)...');
      (stores as any).executeBootSequence('startup').catch((error: unknown) => {
        console.error('StoreProvider: Failed to execute startup boot sequence:', error);
      });
    } else if (stores && stores.initializeApp) {
      // Backward compatibility while all callsites migrate to executeBootSequence.
      console.log('🔧 StoreProvider: executeBootSequence unavailable, falling back to initializeApp...');
      stores.initializeApp().catch((error: unknown) => {
        console.error('StoreProvider: Failed to initialize app:', error);
      });
    } else {
      console.log('🔧 StoreProvider: No stores or boot initialization method available');
    }
  }, [stores]);

  return (
    <StoreContext.Provider value={stores}>
      {children}
    </StoreContext.Provider>
  );
};

interface RespondentStoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  resolved: SecureLoginResponseDto;
}

/**
 * Parallel, purpose-built provider for token-based (non-SRA) administration
 * sessions (/secure, /remote, /link) — deliberately does NOT call
 * `createStoresWithStorage()`. That factory is hard-wired for one SRA/family
 * session per page load (module-level `isStoreInitialized` singleton guard,
 * writes to `__proGroupsStores`/`__childNavStores` globals) and building a
 * full family-oriented store bundle for a single respondent taking one
 * assessment would be both wrong (wrong data model) and risky (singleton
 * guard assumptions). This provider supplies only what `AdminView`/
 * `ReportView` actually read from `useStores()` — confirmed by grep to be
 * exactly `useUserStore` and `useContentStore` — and gives every other hook a
 * loud throw instead of silently misbehaving if something unexpected calls
 * it.
 *
 * `storage` is namespaced internally (see `createNamespacedStorage`) for the
 * session-token store, so a respondent session's `auth_token`/`refresh_token`/
 * `userSession` keys can never collide with an SRA session's identical
 * literal key names if both happen to live in the same browser storage.
 * Content (bundled AdminSegment/AdminAssessment data) is intentionally left
 * unnamespaced — it's public, non-session data safe to share/reuse.
 */
export const RespondentStoreProvider: React.FC<RespondentStoreProviderProps> = ({
  children,
  storage,
  resolved,
}) => {
  const stores = useMemo(() => {
    const sessionStorage = createNamespacedStorage(storage, 'secure_session');
    const useUserStore = createRespondentSessionStore(resolved, sessionStorage);
    const useContentStore = createContentStore(storage);

    const notAvailable = (hookName: string) => (): never => {
      throw new Error(`RespondentStoreProvider: ${hookName} is not available for a token-based administration session`);
    };

    return {
      useUserStore,
      useContentStore,
      useMilestonesStore: notAvailable('useMilestonesStore'),
      useConcernsStore: notAvailable('useConcernsStore'),
      useWellVisitsStore: notAvailable('useWellVisitsStore'),
      useActionsStore: notAvailable('useActionsStore'),
      useProfileStore: notAvailable('useProfileStore'),
      setApiService: (apiService: any) => useUserStore.getState().setApiService(apiService),
      executeBootSequence: async () => {
        console.log('RespondentStoreProvider: executeBootSequence is a no-op — content loading is driven explicitly by the secure administration view.');
      },
      initializeApp: async () => {
        console.log('RespondentStoreProvider: initializeApp is a no-op — content loading is driven explicitly by the secure administration view.');
      },
    };
  }, [storage, resolved]);

  return (
    <StoreContext.Provider value={stores as unknown as StoresType}>
      {children}
    </StoreContext.Provider>
  );
};

// Hooks to use the stores
export const useStores = (): StoresType => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return context;
};

export const useProfileStore = () => {
  const stores = useStores();
  return stores.useProfileStore;
};

export const useUserStore = () => {
  const stores = useStores();
  return stores.useUserStore;
};

export const useMilestonesStore = () => {
  const stores = useStores();
  return stores.useMilestonesStore;
};

export const useConcernsStore = () => {
  const stores = useStores();
  return stores.useConcernsStore;
};

export const useWellVisitsStore = () => {
  const stores = useStores();
  return stores.useWellVisitsStore;
};

export const useActionsStore = () => {
  const stores = useStores();
  return stores.useActionsStore;
};

export const useContentStore = () => {
  const stores = useStores();
  return stores.useContentStore;
};